// CLAUDE.md guardrail: ALL Firebase SDK calls live in this file.
// Other modules import named helpers from here, never `firebase/*` directly.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type Auth,
  type User,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  addDoc,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  type Firestore,
  type DocumentData,
} from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, httpsCallable, type Functions } from "firebase/functions";
import {
  getStorage,
  connectStorageEmulator,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  type FirebaseStorage,
} from "firebase/storage";
import { firebaseConfig, region, adminUids } from "./firebase.config";
import { tierGrantAction } from "./tierGrant";
import { newLessonId } from "./lessonOrdering";
import { cleanAvatar, type Avatar } from "./avatar";
import { excerpt } from "./qa";
import type { CheckpointResponse, CheckpointSet } from "./checkpoints";
import type { Homework } from "./homework";

// ── Emulator switch ──────────────────────────────────────────────────────
// Open the app via http://127.0.0.1:5000 (hosting emulator) → all SDKs point
// at the local Firebase emulators, zero traffic to the real project.
// Open via http://localhost:5173 (pnpm dev) → real project, unchanged.
// Production (school.dewviko.com) can never match 127.0.0.1, so this code is
// dormant there.
const USE_EMULATORS = typeof window !== "undefined" && window.location.hostname === "127.0.0.1";

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let fnsInstance: Functions | null = null;
let storageInstance: FirebaseStorage | null = null;

function ensureApp(): FirebaseApp {
  if (!app) app = initializeApp(firebaseConfig);
  return app;
}

function ensureAuth(): Auth {
  if (!authInstance) {
    authInstance = getAuth(ensureApp());
    if (USE_EMULATORS) connectAuthEmulator(authInstance, "http://127.0.0.1:9099", { disableWarnings: true });
  }
  return authInstance;
}

function ensureDb(): Firestore {
  if (!dbInstance) {
    dbInstance = getFirestore(ensureApp());
    if (USE_EMULATORS) connectFirestoreEmulator(dbInstance, "127.0.0.1", 8085); // not 8080: 5KPlayer AirPlay squats it on Dew's PC
  }
  return dbInstance;
}

function ensureFns(): Functions {
  if (!fnsInstance) {
    fnsInstance = getFunctions(ensureApp(), region);
    if (USE_EMULATORS) connectFunctionsEmulator(fnsInstance, "127.0.0.1", 5001);
  }
  return fnsInstance;
}

function ensureStorage(): FirebaseStorage {
  if (!storageInstance) {
    storageInstance = getStorage(ensureApp());
    if (USE_EMULATORS) connectStorageEmulator(storageInstance, "127.0.0.1", 9199);
  }
  return storageInstance;
}

// ── Auth (email + password) ──────────────────────────────────────────────

export async function signInWithEmail(email: string, password: string): Promise<User> {
  const auth = ensureAuth();
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(ensureAuth(), email);
}

// ── Auth (Google + email link) ───────────────────────────────────────────
// Passwords get forwarded between friends; a Google account or a one-time link
// to the buyer's own inbox does not. New purchases are passwordless - these two
// are the way in. Firebase links the Google credential onto the existing user
// when the email matches, so a student who already bought keeps the same uid
// and every enrollment they have.

const EMAIL_LINK_ADDRESS = "lms:email-link-address";

export async function signInWithGoogle(): Promise<void> {
  const auth = ensureAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  try {
    await signInWithPopup(auth, provider);
  } catch (e) {
    const code = (e as { code?: string }).code ?? "";
    // in-app browsers (Facebook, LINE) and some mobile browsers kill popups
    if (code.includes("popup") || code.includes("operation-not-supported")) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw e;
  }
}

export async function sendLoginLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(ensureAuth(), email, {
    url: `${window.location.origin}/login`,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(EMAIL_LINK_ADDRESS, email);
}

/** Call on /login mount: finishes sign-in when the student arrives from the email link. */
export async function completeLoginLink(): Promise<boolean> {
  const auth = ensureAuth();
  if (!isSignInWithEmailLink(auth, window.location.href)) return false;
  // opened on another device → localStorage is empty, so ask for the address
  const email =
    window.localStorage.getItem(EMAIL_LINK_ADDRESS) ||
    window.prompt("ยืนยันอีเมลที่ขอลิงก์อีกครั้งค่ะ") ||
    "";
  if (!email) return false;
  await signInWithEmailLink(auth, email, window.location.href);
  window.localStorage.removeItem(EMAIL_LINK_ADDRESS);
  return true;
}

export function watchAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(ensureAuth(), cb);
}

export async function signOut(): Promise<void> {
  await fbSignOut(ensureAuth());
}

export function isAdminUser(user: User | null): boolean {
  if (!user) return false;
  return adminUids.includes(user.uid);
}

// ── Firestore reads ──────────────────────────────────────────────────────

export type Course = {
  id: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  trailerVideoRef?: string;
  priceTHB: number;
  stripeProductId?: string;
  stripePriceId?: string;
  published: boolean;
  // Payment tiers that unlock this course ("standard" | "premium" | "vip").
  // createAccount enrolls a buyer into every course whose tiers contain their tier.
  tiers: string[];
  // Display position (1-based) - drag-reordered in admin, drives dashboard order.
  order?: number;
  /** Courses with the same group name show as one big card on the dashboard. */
  group?: string;
};

export type Lesson = {
  id: string;
  courseId: string;
  order: number;
  title: string;
  videoRef?: string;
  materials: { name: string; url: string }[];
  /** V2 rich content authored in BlockNote (Notion-style). Stored as a
   *  typed JSON array of blocks. Authoritative when present. */
  contentBlocks?: unknown[];
  /** V1 markdown content. Read-only fallback for lessons authored before
   *  the BlockNote migration; once a lesson is re-saved it gets a
   *  `contentBlocks` array and this field stops being read. */
  contentMarkdown: string;
  isFree: boolean;
  published: boolean;
  /** Optional human-readable section/group label. Lessons sharing the same
   *  sectionTitle are grouped together in the curriculum sidebar. */
  sectionTitle?: string;
  /** Optional check-in question set shown under the lesson (lib/checkpoints.ts). */
  checkpointSetId?: string;
  /** Optional homework handed in right inside the lesson (lib/homework.ts id). */
  homeworkId?: string;
};

export type Enrollment = {
  id: string;
  userId: string;
  courseId: string;
  grantedAt: Date;
  grantedBy: "stripe" | string;
  revokedAt?: Date;
  revokedReason?: string;
};

export type Progress = {
  lessonId: string;
  userId: string;
  lastPositionSec: number;
  completedAt: Date | null;
  /** Watch map (lib/watchMap.ts): which 10-second parts of the video were really played. */
  watched?: string;
};

function mapCourse(id: string, data: DocumentData): Course {
  return {
    id,
    title: data.title,
    description: data.description,
    thumbnailUrl: data.thumbnailUrl,
    trailerVideoRef: data.trailerVideoRef,
    priceTHB: data.priceTHB,
    stripeProductId: data.stripeProductId,
    stripePriceId: data.stripePriceId,
    published: data.published === true,
    tiers: Array.isArray(data.tiers) ? data.tiers : [],
    order: data.order ?? 0,
    group: data.group ?? "",
  };
}

const byCourseOrder = (a: Course, b: Course) => (a.order ?? 0) - (b.order ?? 0);

export async function getEnrolledCourses(userId: string): Promise<Course[]> {
  const db = ensureDb();
  const enrollSnap = await getDocs(
    query(collection(db, "enrollments"), where("userId", "==", userId), where("revokedAt", "==", null))
  );
  const courseIds = enrollSnap.docs.map((d) => d.data().courseId as string);
  if (courseIds.length === 0) return [];
  const snaps = await Promise.all(courseIds.map((id) => getDoc(doc(db, "courses", id))));
  // Drafts stay admin-only even for people enrolled in them before.
  return snaps.filter((s) => s.exists() && s.data()!.published === true).map((s) => mapCourse(s.id, s.data()!)).sort(byCourseOrder);
}

export async function getCourse(courseId: string): Promise<Course | null> {
  const db = ensureDb();
  const snap = await getDoc(doc(db, "courses", courseId));
  return snap.exists() ? mapCourse(snap.id, snap.data()) : null;
}

// Lessons per course, kept for this page load: the dashboard, course and lesson
// pages all need the same list. The list has no lesson bodies (materials and
// content come back empty): a big course is hundreds of KB of text, so a page
// that shows a body fetches it with getLesson.
// Admin lesson writes below drop the course from the cache; a full reload refetches.
const lessonsCache = new Map<string, Promise<Lesson[]>>();
const forgetLessons = (courseId: string) => lessonsCache.delete(courseId);

export async function getLessons(courseId: string): Promise<Lesson[]> {
  let hit = lessonsCache.get(courseId);
  if (!hit) {
    hit = fetchLessons(courseId);
    hit.catch(() => forgetLessons(courseId));
    lessonsCache.set(courseId, hit);
  }
  return [...(await hit)];
}

const LIST_FIELDS = ["order", "title", "videoRef", "isFree", "published", "sectionTitle", "checkpointSetId", "homeworkId"];

/** Firestore REST value -> plain JS, for the scalar fields in LIST_FIELDS. */
const restValue = (v: Record<string, unknown>) =>
  "integerValue" in v ? Number(v.integerValue) : "nullValue" in v ? null : Object.values(v)[0];

// The web SDK can't fetch only some fields, so the list goes through the REST
// API with a field mask, signed in as the student: same security rules, a few KB
// instead of every lesson body. Emulator runs keep the SDK.
async function lessonRows(courseId: string): Promise<{ id: string; data: Record<string, unknown> }[]> {
  const user = ensureAuth().currentUser;
  if (USE_EMULATORS || !user) {
    const snap = await getDocs(collection(ensureDb(), "courses", courseId, "lessons"));
    return snap.docs.map((d) => ({ id: d.id, data: d.data() }));
  }
  const url =
    `https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}/databases/(default)/documents/courses/` +
    `${encodeURIComponent(courseId)}/lessons?pageSize=300&${LIST_FIELDS.map((f) => `mask.fieldPaths=${f}`).join("&")}`;
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` };
  const rows: { id: string; data: Record<string, unknown> }[] = [];
  let pageToken = "";
  do {
    const res = await fetch(url + (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""), { headers });
    if (!res.ok) throw new Error(`โหลดรายการบทเรียนไม่ได้ (${res.status})`);
    const body = (await res.json()) as {
      documents?: { name: string; fields?: Record<string, Record<string, unknown>> }[];
      nextPageToken?: string;
    };
    for (const d of body.documents ?? []) {
      const data = Object.fromEntries(Object.entries(d.fields ?? {}).map(([k, v]) => [k, restValue(v)]));
      rows.push({ id: d.name.slice(d.name.lastIndexOf("/") + 1), data });
    }
    pageToken = body.nextPageToken ?? "";
  } while (pageToken);
  return rows;
}

async function fetchLessons(courseId: string): Promise<Lesson[]> {
  // Don't orderBy at the server - Firestore excludes docs that lack the order
  // field, which silently hides hand-created lessons. Sort client-side instead.
  const rows = await lessonRows(courseId);
  return rows
    .map(({ id, data }) => {
      return {
        id,
        courseId,
        order: (data.order as number) ?? 0,
        title: (data.title as string) ?? "",
        videoRef: (data.videoRef as string) ?? undefined,
        materials: [],
        contentBlocks: undefined,
        contentMarkdown: "",
        isFree: data.isFree === true,
        published: data.published === true,
        sectionTitle: (data.sectionTitle as string) ?? undefined,
        checkpointSetId: (data.checkpointSetId as string) || undefined,
        homeworkId: (data.homeworkId as string) || undefined,
      };
    })
    .sort((a, b) => a.order - b.order);
}

export async function getLesson(courseId: string, lessonId: string): Promise<Lesson | null> {
  const db = ensureDb();
  const snap = await getDoc(doc(db, "courses", courseId, "lessons", lessonId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    courseId,
    order: data.order ?? 0,
    title: data.title,
    videoRef: data.videoRef,
    materials: data.materials ?? [],
    contentBlocks: Array.isArray(data.contentBlocks) ? (data.contentBlocks as unknown[]) : undefined,
    contentMarkdown: data.contentMarkdown ?? "",
    isFree: data.isFree === true,
    published: data.published === true,
    sectionTitle: data.sectionTitle ?? undefined,
    checkpointSetId: data.checkpointSetId || undefined,
    homeworkId: data.homeworkId || undefined,
  };
}

// ── Storage: lesson image uploads ────────────────────────────────────────

/**
 * Upload an image (selected from BlockNote's image picker / drop) to Firebase
 * Storage and return a publicly readable download URL the editor can embed.
 *
 * Path: `lesson-images/{courseId}/{lessonId}/{timestamp}-{filename}`
 * Storage rules cap to 10 MB and require admin auth.
 */
export async function uploadLessonImage(
  courseId: string,
  lessonId: string,
  file: File
): Promise<string> {
  const storage = ensureStorage();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `lesson-images/${courseId}/${lessonId}/${Date.now()}-${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type });
  return await getDownloadURL(ref);
}

// ── Lesson Q&A ───────────────────────────────────────────────────────────
//
// Two-doc anonymity model:
//   courses/{c}/lessons/{l}/questions/{q}          ← students read this
//     (text, imageUrl, isAnonymous, authorName|null, answer…)  NO uid/email
//   courses/{c}/lessons/{l}/questions/{q}/private/identity  ← admin-only
//     (authorUid, authorEmail)
// Rules enforce that students can never read the private subdoc, so an
// anonymous author's identity is safe even against DevTools.

export type LessonQuestion = {
  id: string;
  text: string;
  imageUrl?: string;
  isAnonymous: boolean;
  authorName: string | null;
  createdAt: Date;
  answerText: string | null;
  answeredAt: Date | null;
};

export async function uploadQuestionImage(
  courseId: string,
  lessonId: string,
  userId: string,
  file: File
): Promise<string> {
  const storage = ensureStorage();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `lesson-questions/${courseId}/${lessonId}/${userId}/${Date.now()}-${safeName}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type });
  return await getDownloadURL(r);
}

/** Admin: upload a course cover image; returns the public download URL. */
/** Upload a lesson video we host ourselves (no Bunny). Returns the playable URL. */
export async function uploadLessonVideo(
  courseId: string,
  lessonId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  return uploadLessonAsset("lesson-videos", courseId, lessonId, file, file.type || "video/mp4", onProgress);
}

/** A downloadable file for a lesson's เอกสารประกอบ (skill file, template, PDF...). */
export async function uploadLessonFile(
  courseId: string,
  lessonId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<string> {
  return uploadLessonAsset("lesson-files", courseId, lessonId, file, file.type || "application/octet-stream", onProgress);
}

async function uploadLessonAsset(
  folder: "lesson-videos" | "lesson-files",
  courseId: string,
  lessonId: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void
): Promise<string> {
  const { uploadBytesResumable } = await import("firebase/storage");
  const storage = ensureStorage();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const ref = storageRef(storage, `${folder}/${courseId}/${lessonId}/${Date.now()}-${safeName}`);
  const task = uploadBytesResumable(ref, file, { contentType });
  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      () => resolve()
    );
  });
  return await getDownloadURL(ref);
}

export async function uploadCourseThumbnail(courseId: string, file: File): Promise<string> {
  const storage = ensureStorage();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `course-thumbnails/${courseId}/${Date.now()}-${safeName}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type });
  return await getDownloadURL(r);
}

export async function postQuestion(
  courseId: string,
  lessonId: string,
  author: { uid: string; email: string | null; displayName: string | null },
  data: { text: string; imageUrl?: string; isAnonymous: boolean }
): Promise<void> {
  const db = ensureDb();
  const col = collection(db, "courses", courseId, "lessons", lessonId, "questions");
  // Public doc - no identifying fields when anonymous.
  const qDoc = await addDoc(col, {
    text: data.text,
    imageUrl: data.imageUrl ?? null,
    isAnonymous: data.isAnonymous,
    authorName: data.isAnonymous ? null : author.displayName ?? null,
    createdAt: serverTimestamp(),
    answerText: null,
    answeredAt: null,
  });
  // Private identity subdoc - admin-only by rules.
  await setDoc(doc(db, qDoc.path, "private", "identity"), {
    authorUid: author.uid,
    authorEmail: author.email,
    authorName: author.displayName ?? null,
  });
  // Tell the instructor(s) there's a new question waiting. Best-effort: a
  // failed notification must never fail the posted question.
  try {
    await Promise.all(
      adminUids.map((adminUid) =>
        addDoc(collection(db, "users", adminUid, "notifications"), {
          type: "question",
          courseId,
          lessonId,
          questionId: qDoc.id,
          questionText: excerpt(data.text),
          authorName: data.isAnonymous ? null : author.displayName ?? null,
          createdAt: serverTimestamp(),
          readAt: null,
        })
      )
    );
  } catch (err) {
    console.error("question saved but admin notification failed", err);
  }
}

function mapQuestion(id: string, data: DocumentData): LessonQuestion {
  return {
    id,
    text: data.text ?? "",
    imageUrl: data.imageUrl ?? undefined,
    isAnonymous: data.isAnonymous === true,
    authorName: data.authorName ?? null,
    createdAt: data.createdAt?.toDate?.() ?? new Date(),
    answerText: data.answerText ?? null,
    answeredAt: data.answeredAt?.toDate?.() ?? null,
  };
}

/** Live subscription to a lesson's questions. Returns an unsubscribe fn. */
export function watchLessonQuestions(
  courseId: string,
  lessonId: string,
  cb: (questions: LessonQuestion[]) => void
): () => void {
  const db = ensureDb();
  const q = query(
    collection(db, "courses", courseId, "lessons", lessonId, "questions"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => mapQuestion(d.id, d.data()))));
}

/** Admin: write an answer onto a question (public doc).
 *  Pass `notify` on a FIRST answer to drop a notification in the author's
 *  inbox (admin reads the private identity subdoc to find them). Omit it when
 *  editing an existing answer - edits must not re-ping the student. */
export async function answerQuestion(
  courseId: string,
  lessonId: string,
  questionId: string,
  answerText: string,
  notify?: { questionText: string }
): Promise<void> {
  const db = ensureDb();
  const qRef = doc(db, "courses", courseId, "lessons", lessonId, "questions", questionId);
  await updateDoc(qRef, {
    answerText,
    answeredAt: serverTimestamp(),
  });
  if (!notify) return;
  // Best-effort: a failed notification must never fail the saved answer.
  try {
    const identity = await getDoc(doc(db, qRef.path, "private", "identity"));
    const authorUid = identity.data()?.authorUid as string | undefined;
    if (!authorUid) return;
    await addDoc(collection(db, "users", authorUid, "notifications"), {
      type: "answer",
      courseId,
      lessonId,
      questionId,
      questionText: excerpt(notify.questionText),
      createdAt: serverTimestamp(),
      readAt: null,
    });
  } catch (err) {
    console.error("answer saved but notification failed", err);
  }
}

// ── Notifications ────────────────────────────────────────────────────────
// users/{uid}/notifications - "answer" docs are written by the admin client
// at answer time; "question" docs are written by the student client into the
// admin's inbox at post time. Each inbox is readable only by its owner, so an
// anonymous author's identity never leaks to other students.

export type AppNotification = {
  id: string;
  type: "answer" | "question" | "certificate";
  courseId: string;
  lessonId: string;
  questionId: string;
  questionText: string;
  /** Asker's display name on "question" notifications; null when anonymous. */
  authorName: string | null;
  createdAt: Date;
  readAt: Date | null;
};

/** Live subscription to the user's latest notifications. Returns unsubscribe fn. */
export function watchNotifications(
  userId: string,
  cb: (items: AppNotification[]) => void
): () => void {
  const db = ensureDb();
  const q = query(
    collection(db, "users", userId, "notifications"),
    orderBy("createdAt", "desc"),
    limit(30)
  );
  return onSnapshot(
    q,
    (snap) =>
      cb(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            type: data.type === "question" || data.type === "homework" ? data.type : ("answer" as const),
            courseId: data.courseId ?? "",
            lessonId: data.lessonId ?? "",
            questionId: data.questionId ?? "",
            questionText: data.questionText ?? "",
            authorName: data.authorName ?? null,
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            readAt: data.readAt?.toDate?.() ?? null,
          };
        })
      ),
    (err) => console.error("notifications subscription failed", err)
  );
}

export async function markNotificationRead(userId: string, notificationId: string): Promise<void> {
  const db = ensureDb();
  await updateDoc(doc(db, "users", userId, "notifications", notificationId), {
    readAt: serverTimestamp(),
  });
}

// ── Progress writes (throttled by caller) ────────────────────────────────

export async function writeProgress(
  userId: string,
  lessonId: string,
  lastPositionSec: number,
  completed: boolean,
  watched?: string
): Promise<void> {
  const db = ensureDb();
  const id = `${userId}_${lessonId}`;
  await setDoc(
    doc(db, "progress", id),
    {
      userId,
      lessonId,
      lastPositionSec,
      // Only ever SET completion here. Writing null on "not yet" used to wipe a
      // finished lesson the moment a student rewatched it. Un-completing is the
      // explicit unmarkLessonComplete button only.
      ...(completed ? { completedAt: serverTimestamp() } : {}),
      ...(watched ? { watched } : {}),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** Admin: every student's watch map for one lesson (feeds the heatmap). */
export async function getLessonWatchMaps(lessonId: string): Promise<string[]> {
  const snap = await getDocs(query(collection(ensureDb(), "progress"), where("lessonId", "==", lessonId)));
  return snap.docs.map((d) => (d.data().watched as string | undefined) ?? "").filter(Boolean);
}

export async function getProgress(userId: string, lessonId: string): Promise<Progress | null> {
  const db = ensureDb();
  const snap = await getDoc(doc(db, "progress", `${userId}_${lessonId}`));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    userId,
    lessonId,
    lastPositionSec: data.lastPositionSec ?? 0,
    completedAt: data.completedAt?.toDate?.() ?? null,
    watched: data.watched ?? "",
  };
}

/**
 * Fetch progress docs for a list of lessons in one batch. Returns a Map keyed by lessonId.
 * Missing entries (no progress yet) are absent from the map.
 */
export async function getProgressForLessons(
  userId: string,
  lessonIds: string[]
): Promise<Map<string, Progress>> {
  const db = ensureDb();
  const map = new Map<string, Progress>();
  if (lessonIds.length === 0) return map;
  // getDoc per lesson - N round-trips. For modest courses (<50 lessons) this is fine.
  // If catalog grows, switch to a where-in query in chunks of 30.
  await Promise.all(
    lessonIds.map(async (lessonId) => {
      const snap = await getDoc(doc(db, "progress", `${userId}_${lessonId}`));
      if (snap.exists()) {
        const data = snap.data();
        map.set(lessonId, {
          userId,
          lessonId,
          lastPositionSec: data.lastPositionSec ?? 0,
          completedAt: data.completedAt?.toDate?.() ?? null,
        });
      }
    })
  );
  return map;
}

export async function markLessonComplete(userId: string, lessonId: string): Promise<void> {
  const db = ensureDb();
  await setDoc(
    doc(db, "progress", `${userId}_${lessonId}`),
    {
      userId,
      lessonId,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function unmarkLessonComplete(userId: string, lessonId: string): Promise<void> {
  const db = ensureDb();
  await setDoc(
    doc(db, "progress", `${userId}_${lessonId}`),
    {
      userId,
      lessonId,
      completedAt: null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// ── Check-ins (private answers, one doc per student per course per set) ──

function mapCheckpoint(data: DocumentData): CheckpointResponse {
  return {
    userId: data.userId ?? "",
    courseId: data.courseId ?? "",
    setId: data.setId ?? "",
    answers: data.answers ?? {},
    note: data.note ?? "",
    skipped: data.skipped === true,
    updatedAt: data.updatedAt?.toDate?.() ?? null,
  };
}

export async function getCheckpointResponse(
  userId: string,
  courseId: string,
  setId: string
): Promise<CheckpointResponse | null> {
  const db = ensureDb();
  const snap = await getDoc(doc(db, "checkpoints", `${userId}_${courseId}_${setId}`));
  return snap.exists() ? mapCheckpoint(snap.data()) : null;
}

export async function saveCheckpointResponse(
  r: Pick<CheckpointResponse, "userId" | "courseId" | "setId" | "answers" | "note" | "skipped">
): Promise<void> {
  const db = ensureDb();
  await setDoc(doc(db, "checkpoints", `${r.userId}_${r.courseId}_${r.setId}`), {
    userId: r.userId,
    courseId: r.courseId,
    setId: r.setId,
    answers: r.answers,
    note: r.note,
    skipped: r.skipped,
    updatedAt: serverTimestamp(),
  });
}

// ── Admin-edited quizzes + homework tasks (Dew, 2026-10-20) ─────────────
// quizSets/{setId} and homeworkTasks/{id} override the defaults written in code
// (lib/quizzes.ts, lib/homework.ts). No doc = the code default is used.

export async function getQuizSetOverride(setId: string): Promise<CheckpointSet | null> {
  const snap = await getDoc(doc(ensureDb(), "quizSets", setId));
  const d = snap.data();
  if (!d || !Array.isArray(d.questions)) return null;
  return { id: setId, title: d.title ?? "", intro: d.intro ?? "", questions: d.questions, quiz: d.quiz === true, noteLabel: d.noteLabel || undefined };
}

export async function saveQuizSet(set: CheckpointSet): Promise<void> {
  await setDoc(doc(ensureDb(), "quizSets", set.id), JSON.parse(JSON.stringify({ ...set, updatedAt: null })));
  await updateDoc(doc(ensureDb(), "quizSets", set.id), { updatedAt: serverTimestamp() });
}

export async function getHomeworkOverrides(): Promise<Map<string, Homework>> {
  const snap = await getDocs(collection(ensureDb(), "homeworkTasks"));
  return new Map(
    snap.docs
      .filter((d) => Array.isArray(d.data().deliver))
      .map((d) => {
        const x = d.data();
        return [d.id, { id: d.id, module: x.module, deliver: x.deliver, pass: x.pass ?? [], time: x.time || undefined }];
      })
  );
}

export async function saveHomeworkTask(h: Homework): Promise<void> {
  await setDoc(doc(ensureDb(), "homeworkTasks", h.id), { ...JSON.parse(JSON.stringify(h)), updatedAt: serverTimestamp() });
}

// ── Homework (AI Money Factory การบ้าน + Quiz page) ─────────────────────
// homework/{uid}_{homeworkId}: one submission per student per homework, editable by its owner.

export type HomeworkSubmission = {
  homeworkId: string; courseId: string; text: string; link: string; updatedAt: Date | null;
};

const mapHomework = (x: Record<string, any>): HomeworkSubmission => ({
  homeworkId: x.homeworkId, courseId: x.courseId, text: x.text ?? "", link: x.link ?? "",
  updatedAt: x.updatedAt?.toDate?.() ?? null,
});


// ── Certificates (AI Money Factory) ───────────────────────────────────────
export type Certificate = {
  id: string; userId: string; program: string; name: string; avatar?: unknown;
  /** pending = waiting for Dew, approved = live + public, rejected = sent back with a note (student can ask again). */
  status: "pending" | "approved" | "rejected"; note: string; requestedAt: Date | null; issuedAt: Date | null;
};

const mapCertificate = (id: string, d: Record<string, any>): Certificate => ({
  id, userId: d.userId, program: d.program, name: d.name, avatar: d.avatar, status: d.status ?? "pending", note: d.note ?? "",
  requestedAt: d.requestedAt?.toDate?.() ?? null, issuedAt: d.issuedAt?.toDate?.() ?? null,
});

export async function callClaimCertificate(name: string): Promise<string> {
  const res = await httpsCallable<{ name: string }, { certId: string }>(ensureFns(), "claimCertificate")({ name });
  return res.data.certId;
}

export async function getCertificate(id: string): Promise<Certificate | null> {
  const d = (await getDoc(doc(ensureDb(), "certificates", id))).data();
  return d ? mapCertificate(id, d) : null;
}

export async function getMyCertificate(userId: string, program: string): Promise<Certificate | null> {
  const snap = await getDocs(query(collection(ensureDb(), "certificates"), where("userId", "==", userId), where("program", "==", program), limit(1)));
  const d = snap.docs[0];
  return d ? mapCertificate(d.id, d.data()) : null;
}

/** Admin: certificate requests waiting for approval, oldest first. */
export async function getPendingCertificates(): Promise<Certificate[]> {
  const snap = await getDocs(query(collection(ensureDb(), "certificates"), where("status", "==", "pending")));
  return snap.docs.map((d) => mapCertificate(d.id, d.data()))
    .sort((a, b) => (a.requestedAt?.getTime() ?? 0) - (b.requestedAt?.getTime() ?? 0));
}

/** Admin: approve (goes live, dated today) or send back with a note. */
export async function decideCertificate(id: string, status: "approved" | "rejected", note: string): Promise<void> {
  await updateDoc(doc(ensureDb(), "certificates", id), { status, note, issuedAt: status === "approved" ? serverTimestamp() : null });
}

export async function getMyHomework(userId: string): Promise<Map<string, HomeworkSubmission>> {
  const snap = await getDocs(query(collection(ensureDb(), "homework"), where("userId", "==", userId)));
  return new Map(
    snap.docs.map((d) => {
      const x = d.data();
      return [x.homeworkId as string, mapHomework(x)];
    })
  );
}

export async function saveHomework(userId: string, s: Omit<HomeworkSubmission, "updatedAt">): Promise<void> {
  await setDoc(doc(ensureDb(), "homework", `${userId}_${s.homeworkId}`), {
    userId,
    homeworkId: s.homeworkId,
    courseId: s.courseId,
    text: s.text,
    link: s.link,
    updatedAt: serverTimestamp(),
  });
}

/** Admin-only: every submission, newest first. */
export async function getAllHomework(): Promise<(HomeworkSubmission & { userId: string })[]> {
  const snap = await getDocs(collection(ensureDb(), "homework"));
  return snap.docs
    .map((d) => {
      const x = d.data();
      return { userId: x.userId as string, ...mapHomework(x) };
    })
    .sort((a, b) => (b.updatedAt?.getTime() ?? 0) - (a.updatedAt?.getTime() ?? 0));
}

/** Admin-only (rules gate the collection read). Fine at current scale; paginate if it grows. */
export async function getAllCheckpointResponses(): Promise<CheckpointResponse[]> {
  const db = ensureDb();
  const snap = await getDocs(collection(db, "checkpoints"));
  return snap.docs.map((d) => mapCheckpoint(d.data()));
}

// ── Admin: progress dashboard ────────────────────────────────────────────

export type ProgressRow = {
  userId: string;
  lessonId: string;
  completedAt: Date | null;
  lastPositionSec: number;
  watched?: string;
};

/**
 * Read the entire `progress` collection. Admin-only (firestore.rules gates
 * the collection read on isAdmin()). Fine at the current scale (one course,
 * tens of students → hundreds of docs); if progress volume grows large,
 * switch to per-course paths or an aggregation.
 */
export async function getAllProgress(): Promise<ProgressRow[]> {
  const db = ensureDb();
  const snap = await getDocs(collection(db, "progress"));
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      userId: data.userId ?? "",
      lessonId: data.lessonId ?? "",
      completedAt: data.completedAt?.toDate?.() ?? null,
      lastPositionSec: data.lastPositionSec ?? 0,
      watched: data.watched ?? "",
    };
  });
}

// ── Admin: enrollment / student management ───────────────────────────────

export type EnrollmentRow = {
  enrollmentId: string;
  userId: string;
  courseId: string;
  grantedAt: Date | null;
  grantedBy: string;
  revokedAt: Date | null;
  revokedReason?: string;
  email?: string;
  courseTitle?: string;
  tier?: string;
};

export async function getAllEnrollments(): Promise<EnrollmentRow[]> {
  const db = ensureDb();
  const enrollSnap = await getDocs(collection(db, "enrollments"));

  const userIds = new Set<string>();
  const courseIds = new Set<string>();
  enrollSnap.docs.forEach((d) => {
    const data = d.data();
    if (data.userId) userIds.add(data.userId);
    if (data.courseId) courseIds.add(data.courseId);
  });

  const userEmails = new Map<string, string>();
  const userTiers = new Map<string, string>();
  const courseTitles = new Map<string, string>();

  await Promise.all([
    ...[...userIds].map(async (uid) => {
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) {
        userEmails.set(uid, snap.data().email ?? "-");
        if (snap.data().tier) userTiers.set(uid, snap.data().tier);
      }
    }),
    ...[...courseIds].map(async (cid) => {
      const snap = await getDoc(doc(db, "courses", cid));
      if (snap.exists()) courseTitles.set(cid, snap.data().title ?? cid);
    }),
  ]);

  return enrollSnap.docs.map((d) => {
    const data = d.data();
    return {
      enrollmentId: d.id,
      userId: data.userId ?? "",
      courseId: data.courseId ?? "",
      grantedAt: data.grantedAt?.toDate?.() ?? null,
      grantedBy: data.grantedBy ?? "-",
      revokedAt: data.revokedAt?.toDate?.() ?? null,
      revokedReason: data.revokedReason,
      email: userEmails.get(data.userId),
      courseTitle: courseTitles.get(data.courseId),
      tier: userTiers.get(data.userId),
    };
  });
}

/**
 * Admin: set a student's tier and grant every course that tier unlocks.
 * Grant-only - never revokes an existing enrollment (dispute-safe).
 * Returns the courseIds that were newly granted or restored.
 */
/** Give one student one course: enroll, or un-revoke a revoked enrollment. Returns false if already active. */
export async function grantCourse(userId: string, courseId: string): Promise<boolean> {
  const db = ensureDb();
  const ref = doc(db, "enrollments", `${userId}_${courseId}`);
  const existing = await getDoc(ref);
  const action = tierGrantAction(
    existing.exists() ? { revokedAt: existing.data().revokedAt?.toDate?.() ?? null } : null
  );
  if (action === "create") {
    await setDoc(ref, {
      userId,
      courseId,
      grantedAt: serverTimestamp(),
      grantedBy: "admin",
      stripeSessionId: null,
      revokedAt: null,
    });
  } else if (action === "restore") {
    await updateDoc(ref, { revokedAt: null, revokedReason: null });
  }
  return action !== "skip";
}

/** Give a student every course tagged with `tier`. Never revokes (dispute-safe). Returns the courses added. */
export async function setStudentTier(userId: string, tier: string): Promise<string[]> {
  const db = ensureDb();
  const coursesSnap = await getDocs(query(collection(db, "courses"), where("tiers", "array-contains", tier)));
  const granted: string[] = [];
  for (const c of coursesSnap.docs) if (await grantCourse(userId, c.id)) granted.push(c.id);
  return granted;
}

export async function revokeEnrollment(enrollmentId: string, reason: string): Promise<void> {
  const db = ensureDb();
  await updateDoc(doc(db, "enrollments", enrollmentId), {
    revokedAt: serverTimestamp(),
    revokedReason: reason,
  });
}

export async function restoreEnrollment(enrollmentId: string): Promise<void> {
  const db = ensureDb();
  await updateDoc(doc(db, "enrollments", enrollmentId), {
    revokedAt: null,
    revokedReason: null,
  });
}

// ── Admin: course CRUD ───────────────────────────────────────────────────

export async function getAllCoursesForAdmin(): Promise<Course[]> {
  const db = ensureDb();
  const snap = await getDocs(query(collection(db, "courses")));
  return snap.docs.map((d) => mapCourse(d.id, d.data())).sort(byCourseOrder);
}

/** Minimal batch write from reorderWrites() - mirrors reorderLessons. */
export async function reorderCourses(writes: { id: string; order: number }[]): Promise<void> {
  const db = ensureDb();
  const batch = writeBatch(db);
  for (const w of writes) {
    batch.update(doc(db, "courses", w.id), { order: w.order });
  }
  await batch.commit();
}

export async function createCourse(
  courseId: string,
  data: Omit<Course, "id">
): Promise<void> {
  const db = ensureDb();
  await setDoc(doc(db, "courses", courseId), stripUndefined(data));
}

export async function updateCourse(
  courseId: string,
  data: Partial<Omit<Course, "id">>
): Promise<void> {
  const db = ensureDb();
  await updateDoc(doc(db, "courses", courseId), stripUndefined(data));
}

export async function deleteCourse(courseId: string): Promise<void> {
  const { deleteDoc } = await import("firebase/firestore");
  const db = ensureDb();
  await deleteDoc(doc(db, "courses", courseId));
}

// ── Admin: lesson CRUD ───────────────────────────────────────────────────

/** Firestore rejects writes that contain `undefined` values anywhere in the
 *  payload - including deeply nested fields inside arrays/objects (e.g.
 *  BlockNote block props that left some optional field unset). JSON
 *  serialization is the fastest, no-dependency way to strip them: undefined
 *  values are dropped during JSON.stringify, and we get a clean structured
 *  clone back. The trade-off is that Date instances become ISO strings - 
 *  we don't write Dates from this path, so it's safe. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return JSON.parse(JSON.stringify(obj)) as Partial<T>;
}

export async function createLesson(
  courseId: string,
  lessonId: string,
  data: Omit<Lesson, "id" | "courseId">
): Promise<void> {
  const db = ensureDb();
  await setDoc(doc(db, "courses", courseId, "lessons", lessonId), stripUndefined(data));
  forgetLessons(courseId);
}

export async function updateLesson(
  courseId: string,
  lessonId: string,
  data: Partial<Omit<Lesson, "id" | "courseId">>
): Promise<void> {
  const db = ensureDb();
  await updateDoc(doc(db, "courses", courseId, "lessons", lessonId), stripUndefined(data));
  forgetLessons(courseId);
}

/** New empty draft lesson appended after the last one; returns its auto id.
 *  Drafts are unpublished, so students never see them until the admin ticks เผยแพร่. */
export async function createDraftLesson(courseId: string): Promise<string> {
  const id = newLessonId();
  const maxOrder = (await getLessons(courseId)).reduce((m, l) => Math.max(m, l.order), 0);
  await createLesson(courseId, id, {
    order: maxOrder + 1,
    title: "",
    videoRef: "",
    materials: [],
    contentBlocks: undefined,
    contentMarkdown: "",
    isFree: false,
    published: false,
    sectionTitle: "",
  });
  return id;
}

export async function deleteLesson(courseId: string, lessonId: string): Promise<void> {
  const { deleteDoc } = await import("firebase/firestore");
  const db = ensureDb();
  await deleteDoc(doc(db, "courses", courseId, "lessons", lessonId));
  forgetLessons(courseId);
}

/**
 * Persist a new display order for a course's lessons in one atomic batch.
 * Callers compute the minimal write set with `reorderWrites()` from
 * lessonOrdering.ts - typically 2 docs for an adjacent swap, not the
 * whole list.
 */
export async function reorderLessons(
  courseId: string,
  writes: { id: string; order: number }[]
): Promise<void> {
  if (writes.length === 0) return;
  const db = ensureDb();
  const batch = writeBatch(db);
  for (const w of writes) {
    batch.update(doc(db, "courses", courseId, "lessons", w.id), { order: w.order });
  }
  await batch.commit();
  forgetLessons(courseId);
}

// ── User profile ─────────────────────────────────────────────────────────

export async function ensureUserDoc(user: User): Promise<void> {
  const db = ensureDb();
  await setDoc(
    doc(db, "users", user.uid),
    {
      email: user.email,
      displayName: user.displayName ?? null,
      lastSeenAt: serverTimestamp(),
    },
    { merge: true }
  );
}


// ── Callable functions ───────────────────────────────────────────────────

export async function callRequestAccountDeletion(): Promise<void> {
  const fn = httpsCallable(ensureFns(), "requestAccountDeletion");
  await fn({});
}

export type StudentDirectoryEntry = { email: string | null; displayName: string | null; neverLoggedIn: boolean };

/**
 * Admin-only: fetch every student's email (from Auth) + displayName, keyed by
 * uid. Fills in emails for students who paid but never logged in (so have no
 * users/ profile doc). Returns a Map for easy lookup.
 */
export async function callGetStudentDirectory(): Promise<Map<string, StudentDirectoryEntry>> {
  const fn = httpsCallable<Record<string, never>, { directory: Record<string, StudentDirectoryEntry> }>(
    ensureFns(),
    "getStudentDirectory"
  );
  const res = await fn({});
  return new Map(Object.entries(res.data.directory));
}

export async function callGetSignedVideoUrl(
  courseId: string,
  lessonId: string
): Promise<{ url: string; expiresAt: number }> {
  const fn = httpsCallable<{ courseId: string; lessonId: string }, { url: string; expiresAt: number }>(
    ensureFns(),
    "getSignedVideoUrl"
  );
  const res = await fn({ courseId, lessonId });
  return res.data;
}

// ── Community (Skool-style feed + leaderboard) ───────────────────────────
// Membership, counters and points are server-owned (functions/src/community.ts).
// The client only writes posts, comments and its own like docs.

export type CommunityPost = {
  id: string;
  brand: string;
  authorUid: string;
  authorName: string;
  category: string;
  title: string;
  body: string;
  pinned: boolean;
  likeCount: number;
  commentCount: number;
  createdAt: Date;
};

export type CommunityComment = { id: string; authorUid: string; authorName: string; text: string; createdAt: Date };

export type CommunityMember = {
  uid: string;
  displayName: string;
  points: number;
  lessonsDone: number;
  minutesActive: number;
  avatar?: Avatar;
  /** Student turned "show me on the leaderboard" off. */
  hidden?: boolean;
};

let memberCheck: Promise<boolean> | null = null;
/** Server checks enrollments, (un)registers this user as a member and recounts
 *  their finished lessons. Once per page load; a failed call is retried next time. */
export function callEnsureCommunityMember(): Promise<boolean> {
  if (!memberCheck) {
    const fn = httpsCallable<object, { member: boolean }>(ensureFns(), "ensureCommunityMember");
    memberCheck = fn({}).then((r) => r.data.member);
    memberCheck.catch(() => (memberCheck = null));
  }
  return memberCheck;
}

/** Leaderboard time-on-site: the server counts at most one minute per minute. */
export async function callRecordActiveMinute(): Promise<void> {
  await httpsCallable(ensureFns(), "recordActiveMinute")({});
}

function mapPost(id: string, d: DocumentData): CommunityPost {
  return {
    id,
    brand: d.brand,
    authorUid: d.authorUid,
    authorName: d.authorName ?? "สมาชิก",
    category: d.category ?? "",
    title: d.title ?? "",
    body: d.body ?? "",
    pinned: d.pinned === true,
    likeCount: d.likeCount ?? 0,
    commentCount: d.commentCount ?? 0,
    createdAt: d.createdAt?.toDate?.() ?? new Date(),
  };
}

// shortcut: newest 100 posts only, no paging; add a "load more" cursor once the feed outgrows it.
export function watchCommunityPosts(brandId: string, cb: (posts: CommunityPost[]) => void): () => void {
  const q = query(
    collection(ensureDb(), "communityPosts"),
    where("brand", "==", brandId),
    orderBy("createdAt", "desc"),
    limit(100)
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => mapPost(d.id, d.data()))));
}

export function watchCommunityPost(postId: string, cb: (post: CommunityPost | null) => void): () => void {
  return onSnapshot(doc(ensureDb(), "communityPosts", postId), (d) => cb(d.exists() ? mapPost(d.id, d.data()) : null));
}

export async function createCommunityPost(
  author: { uid: string; displayName: string | null },
  data: { brand: string; category: string; title: string; body: string }
): Promise<string> {
  const ref = await addDoc(collection(ensureDb(), "communityPosts"), {
    ...data,
    authorUid: author.uid,
    authorName: author.displayName || "สมาชิก",
    pinned: false,
    likeCount: 0,
    commentCount: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteCommunityPost(postId: string): Promise<void> {
  await deleteDoc(doc(ensureDb(), "communityPosts", postId));
}

/** Admin only (rules). */
export async function setPostPinned(postId: string, pinned: boolean): Promise<void> {
  await updateDoc(doc(ensureDb(), "communityPosts", postId), { pinned });
}

export async function setPostLiked(postId: string, userId: string, liked: boolean): Promise<void> {
  const ref = doc(ensureDb(), "communityPosts", postId, "likes", userId);
  if (liked) await setDoc(ref, { userId, createdAt: serverTimestamp() });
  else await deleteDoc(ref);
}

/** Live set of post ids this user has liked. */
export function watchMyLikes(userId: string, cb: (postIds: Set<string>) => void): () => void {
  const q = query(collectionGroup(ensureDb(), "likes"), where("userId", "==", userId));
  return onSnapshot(q, (snap) => cb(new Set(snap.docs.map((d) => d.ref.parent.parent!.id))));
}

export function watchComments(postId: string, cb: (comments: CommunityComment[]) => void): () => void {
  const q = query(collection(ensureDb(), "communityPosts", postId, "comments"), orderBy("createdAt", "asc"));
  return onSnapshot(q, (snap) =>
    cb(
      snap.docs.map((d) => ({
        id: d.id,
        authorUid: d.data().authorUid,
        authorName: d.data().authorName ?? "สมาชิก",
        text: d.data().text ?? "",
        createdAt: d.data().createdAt?.toDate?.() ?? new Date(),
      }))
    )
  );
}

export async function addComment(postId: string, author: { uid: string; displayName: string | null }, text: string): Promise<void> {
  await addDoc(collection(ensureDb(), "communityPosts", postId, "comments"), {
    authorUid: author.uid,
    authorName: author.displayName || "สมาชิก",
    text,
    createdAt: serverTimestamp(),
  });
}

export async function deleteComment(postId: string, commentId: string): Promise<void> {
  await deleteDoc(doc(ensureDb(), "communityPosts", postId, "comments", commentId));
}

// shortcut: reads every member (fine for a few thousand); switch to per-author reads if it grows past that.
export function watchMembers(cb: (members: CommunityMember[]) => void): () => void {
  return onSnapshot(collection(ensureDb(), "members"), (snap) =>
    cb(
      snap.docs.map((d) => ({
        uid: d.id,
        displayName: d.data().displayName ?? "สมาชิก",
        points: d.data().points ?? 0,
        lessonsDone: d.data().lessonsDone ?? 0,
        minutesActive: d.data().minutesActive ?? 0,
        avatar: d.data().avatar ? cleanAvatar(d.data().avatar) : undefined,
        hidden: d.data().hidden === true,
      }))
    )
  );
}

/** A lesson Q&A question, shown in the Chat โลก feed next to posts. */
export type FeedQuestion = LessonQuestion & { courseId: string; lessonId: string; lessonTitle: string };

// shortcut: one small query per lesson, because rules only let a student read questions
// of courses they are enrolled in; move to a server-built feed if lessons pass a few hundred.
export async function getRecentLessonQuestions(userId: string, isAdmin: boolean): Promise<FeedQuestion[]> {
  const db = ensureDb();
  const courses = isAdmin ? await getAllCoursesForAdmin() : await getEnrolledCourses(userId);
  const lessons = (await Promise.all(courses.map((c) => getLessons(c.id))))
    .flat()
    .filter((l) => l.published || isAdmin);
  const lists = await Promise.all(
    lessons.map((l) =>
      getDocs(query(collection(db, "courses", l.courseId, "lessons", l.id, "questions"), orderBy("createdAt", "desc"), limit(10)))
        .then((snap) =>
          snap.docs.map((d) => ({ ...mapQuestion(d.id, d.data()), courseId: l.courseId, lessonId: l.id, lessonTitle: l.title }))
        )
        .catch(() => [] as FeedQuestion[]) // one unreadable lesson must not blank the whole feed
    )
  );
  return lists.flat();
}

// ── Student profile (welcome page, Dew 2026-10-18) ───────────────────────
// Lives on users/{uid}. `profileCompletedAt` set = the welcome page never shows again.

export type UserProfile = {
  nickname: string;
  /** Optional AKA (display handle); the shown name is aka || nickname. */
  aka: string;
  avatar: Avatar;
  occupation: string;
  goal: string;
  question: string;
  showOnLeaderboard: boolean;
  completed: boolean;
};

export async function getUserProfile(userId: string): Promise<UserProfile> {
  const d = (await getDoc(doc(ensureDb(), "users", userId))).data() ?? {};
  return {
    nickname: d.nickname ?? "",
    aka: d.aka ?? "",
    avatar: cleanAvatar(d.avatar),
    occupation: d.occupation ?? "",
    goal: d.goal ?? "",
    question: d.question ?? "",
    showOnLeaderboard: d.showOnLeaderboard !== false,
    completed: !!d.profileCompletedAt,
  };
}

export async function saveUserProfile(userId: string, p: Omit<UserProfile, "completed">): Promise<void> {
  await setDoc(
    doc(ensureDb(), "users", userId),
    {
      nickname: p.nickname.trim().slice(0, 40),
      aka: p.aka.trim().slice(0, 40),
      avatar: cleanAvatar(p.avatar),
      occupation: p.occupation,
      goal: p.goal.trim().slice(0, 500),
      question: p.question.trim().slice(0, 500),
      showOnLeaderboard: p.showOnLeaderboard,
      profileCompletedAt: serverTimestamp(),
    },
    { merge: true }
  );
  memberCheck = null; // next ensureCommunityMember copies the new name/avatar onto members/{uid}
}
